<template>
  <Tabs v-model:value="value">
    <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
      <slot :name="slotName" v-bind="slotProps ?? {}" />
    </template>
  </Tabs>
</template>

<script setup lang="ts">
import type { TabsProps } from "primevue";

import { Tabs } from "primevue";

const value = defineModel<number | string>("value", { required: true });

defineProps</* @vue-ignore */ Omit<TabsProps, "value">>();
</script>

<style lang="postcss">
.p-tabs {
  @apply flex flex-col;

  &-scrollable > .p-tablist {
    @apply overflow-hidden;
  }
}

.p-tablist {
  @apply relative flex;

  &-viewport {
    @apply overflow-x-auto overflow-y-hidden overscroll-y-contain overscroll-x-auto [scrollbar-behavior:smooth] [scrollbar-width:none];

    &::-webkit-scrollbar {
      @apply hidden;
    }
  }

  &-tab-list {
    @apply relative flex border-b border-surface-200 bg-surface-0;
  }

  &-content {
    @apply flex-grow;
  }

  &-nav-button {
    @apply !absolute top-0 z-20 flex h-full w-10 flex-shrink-0 cursor-pointer items-center justify-center bg-surface-0 text-surface-500 shadow-[0px_0px_10px_50px_rgba(255,255,255,0.6)] transition-colors duration-200 hover:text-surface-700 focus-visible:z-10 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-[-1px] focus-visible:outline-primary;
  }

  &-prev-button {
    @apply start-0;
  }

  &-next-button {
    @apply end-0;
  }

  &-prev-button:dir(rtl),
  &-next-button:dir(rtl) {
    @apply rotate-180;
  }

  &-active-bar {
    @apply absolute -bottom-px z-10 block h-px bg-primary transition-[left,width] duration-200 ease-[cubic-bezier(0.35,0,0.25,1)];
  }
}

.p-tab {
  @apply relative -mb-px flex-shrink-0 cursor-pointer select-none whitespace-nowrap border-b border-surface-200 px-[1.125rem] py-4 font-semibold text-surface-500 transition-colors duration-200;

  &.p-disabled {
    @apply cursor-default;
  }

  &:not(.p-disabled):focus-visible {
    @apply z-10 outline outline-1 outline-offset-[-1px] outline-primary;
  }

  &:not(.p-tab-active, .p-disabled):hover {
    @apply text-surface-700;
  }

  &:not(.p-disabled):hover {
    @apply bg-primary-50;
  }

  &-active {
    @apply border-primary text-primary;
  }
}

.p-tabpanels {
  @apply bg-surface-0 px-[1.125rem] pb-[1.125rem] pt-[0.875rem] text-surface-700 outline-none;
}
</style>
